import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import LogInButtons from 'flarum/forum/components/LogInButtons';
import LogInButton from 'flarum/forum/components/LogInButton';
import extractText from 'flarum/common/utils/extractText';
import Tooltip from 'flarum/common/components/Tooltip';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import ForumApplication from 'flarum/forum/ForumApplication';
import { openOAuthPopup } from '../utils/popupUtils';

import type LinkedAccount from '../models/LinkedAccount';
import type Mithril from 'mithril';
import type ItemList from 'flarum/common/utils/ItemList';

export type OAuthProvider = {
  name: string;
  icon: string;
  priority: number;
} | null;

export default function () {
  extend(LogInButton, 'initAttrs', function (_returnedValue, attrs) {
    attrs.onclick = function () {
      openOAuthPopup(app, attrs);
    };
  });

  extend(LogInButtons.prototype, 'items', function (items: ItemList<Mithril.Children>) {
    const onlyIcons = app.forum.attribute<boolean>('fof-oauth.only_icons');
    const enabledOAuthProviders = app.forum
      .attribute<OAuthProvider[]>('fof-oauth')
      .filter((provider): provider is NonNullable<OAuthProvider> => provider !== null);

    enabledOAuthProviders.forEach(({ name, icon, priority }) => {
      let className = `Button FoFLogInButton LogInButton--${name}`;

      if (onlyIcons) {
        className += ' Button--icon';
      }

      items.add(
        name,
        <div className={`LogInButtonContainer LogInButtonContainer--${name}`}>
          <LogInButton className={className} icon={icon} path={`/auth/${name}`} disabled={app.fof_oauth_loginInProgress}>
            {app.translator.trans(`fof-oauth.forum.log_in.with_${name}_button`, {
              provider: app.translator.trans(`fof-oauth.forum.providers.${name}`),
            })}
          </LogInButton>
        </div>,
        priority
      );
    });
  });

  override(LogInButton.prototype, 'view', function (original, vnode: Mithril.VnodeDOM) {
    const onlyIcons = app.forum.attribute<boolean>('fof-oauth.only_icons');
    if (!onlyIcons) return original(vnode);

    const child = original(vnode);

    // @ts-ignore
    return <Tooltip text={extractText(child.children[1])}>{child}</Tooltip>;
  });

  extend(LogInButtons.prototype, 'view', function (vdom) {
    const onlyIcons = app.forum.attribute<boolean>('fof-oauth.only_icons');
    if (!onlyIcons) return;

    // @ts-ignore
    vdom.attrs.className += ' FoFLogInButtons--icons';
  });

  extend(ForumApplication.prototype, 'authenticationComplete', function (_, payload) {
    if (payload.loggedIn) {
      app.fof_oauth_loginInProgress = true;
      // This will automatically be reset, as authenticationComplete also triggers a window reload.

      m.redraw();
    }
  });

  ForumApplication.prototype.linkingComplete = async function () {
    try {
      app.fof_oauth_linkingInProgress = true;
      m.redraw();

      // Refresh the list of providers
      const newProviders = await this.store.find<LinkedAccount[]>('linked-accounts');

      // Get the old IDs of the new providers (the one(s) that have just been linked)
      const newProviderOldIds = newProviders.filter((p) => p.providerIdentifier() !== null).map((p) => `${app.session.user!.id()}-${p.name()}`);

      // The store will contain an old version of the login provider (unlinked) that has
      // another ID than the new one (linked). We need to delete that one from the store
      // so that the UI gets updated correctly.

      // Find the old providers (match the old ID format: <userId>-<providerName>)
      const oldProviders = newProviderOldIds
        .map((oldId) => this.store.getById<LinkedAccount>('linked-accounts', oldId))
        .filter((p): p is LinkedAccount => !!p);

      if (oldProviders.length) {
        for (const oldProvider of oldProviders) {
          // @ts-ignore
          delete this.store.data['linked-accounts'][oldProvider.id()];
        }
      } else {
        // This may just be a signup/login flow, so reload the page to sign in the user.
        window.location.reload();
        return;
      }

      const userId = app.session?.user?.id();

      if (!userId) throw new Error('User ID not available');

      // Refresh the session user
      await this.store.find('users', userId);

      app.fof_oauth_linkingInProgress = false;
      m.redraw();
    } catch (error) {
      app.fof_oauth_linkingInProgress = false;
      m.redraw();
    }
  };

  extend(LogInModal.prototype, 'onbeforeupdate', function () {
    if (app.fof_oauth_loginInProgress) {
      // @ts-ignore
      this.loading = true;
    }
  });

  extend(SignUpModal.prototype, 'onbeforeupdate', function () {
    if (app.fof_oauth_loginInProgress) {
      // @ts-ignore
      this.loading = true;
    }
  });

  extend(SignUpModal.prototype, 'fields', function (items: ItemList<unknown>) {
    // If a suggested username was not provided by the OAuth service, display some help text to the user.
    if (!!this.attrs.token && !this.attrs.username) {
      items.add(
        'username-help',
        <div>
          <p>{app.translator.trans('fof-oauth.forum.signup.username_help')}</p>
        </div>,
        35
      );
    }
  });
}
